import { DatePicker } from '@artic/ui/date-picker';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';

import { formatIsoDate, parseIsoDate } from '../../components/iso-date';
import {
  DATE_FORMAT_OPTIONS,
  DEFAULT_DATE_FORMAT_KEY,
  type StructuredColumnDateFormatKey,
} from '../structured-columns/date-formats';

/**
 * Native `<input>`-backed cell editors for the metadata columns. They follow
 * AG Grid's controlled editor pattern (value + onValueChange) so the parent's
 * onCellValueChanged hook receives string values that the backend's metadata
 * coercion path already understands.
 *
 * The shape mirrors `DropdownCellEditor` so an editor for SelectOne can stay
 * shared.
 */
function useFocusOnMount<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

function emitOrClear(
  props: Pick<CustomCellEditorProps, 'onValueChange' | 'stopEditing'>,
  raw: string,
) {
  if (raw === '') {
    props.onValueChange(null);
  } else {
    props.onValueChange(raw);
  }
}

export function MetadataStringCellEditor(props: CustomCellEditorProps) {
  const ref = useFocusOnMount<HTMLInputElement>();
  const value = (props.value as string | null | undefined) ?? '';
  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => emitOrClear(props, e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.stopEditing();
      }}
      onBlur={() => props.stopEditing()}
      className="h-full w-full border-none bg-transparent px-2 text-sm outline-none"
    />
  );
}

export function MetadataNumberCellEditor(props: CustomCellEditorProps) {
  const ref = useFocusOnMount<HTMLInputElement>();
  const initial = (() => {
    const v = props.value;
    if (v === null || v === undefined) return '';
    if (typeof v === 'number') return String(v);
    return String(v);
  })();
  return (
    <input
      ref={ref}
      type="number"
      defaultValue={initial}
      onChange={(e) => emitOrClear(props, e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.stopEditing();
      }}
      onBlur={() => props.stopEditing()}
      className="h-full w-full border-none bg-transparent px-2 text-sm outline-none"
    />
  );
}

export interface MetadataDateCellEditorParams {
  /**
   * Partner-configured date format key (Structured Columns). When set, the
   * DatePicker uses the matching date-fns format string for input + display.
   * Omitted / null → falls back to the iso_8601 default (`yyyy-MM-dd`).
   */
  dateFormatKey?: StructuredColumnDateFormatKey | null;
}

/**
 * Date cell editor backed by the shadcn input-style `<DatePicker>`: a typed
 * input on the left, a calendar trigger on the right, and the calendar
 * popover below. Stored values are read/written as `YYYY-MM-DD` regardless
 * of display format — both Postgres `date` and `timestamptz` columns accept
 * that on write, and parsing trims the first 10 chars to be tolerant of
 * stored ISO 8601 timestamps.
 *
 * Picking a date from the calendar (or pressing Enter on a valid typed
 * value) commits and closes; Escape cancels. The input auto-focuses on
 * mount so the user can immediately start typing.
 *
 * Enter handling: AG Grid attaches a NATIVE keydown listener on the grid
 * container — that listener fires during the DOM bubble phase BEFORE
 * React's delegated `onKeyDown` reaches the DatePicker's input. Without
 * intervention AG Grid commits the original (initial) value and unmounts
 * the editor before the DatePicker's `commitDraft` can fire our
 * `onValueChange` callback, so the typed value is lost on Enter. (Tab
 * dodges this because the focus move triggers `onBlur` → `commitDraft`
 * synchronously before the Tab keydown bubbles to AG Grid.)
 *
 * The fix lives on the colDef: `suppressKeyboardEvent` returns `true` for
 * Enter while editing, so AG Grid's default Enter handler is suppressed
 * and the DatePicker's React handler runs cleanly. See `columns.tsx`
 * where the date / timestamptz column definitions wire this up.
 */
export function MetadataDateCellEditor(
  props: CustomCellEditorProps & MetadataDateCellEditorParams,
) {
  const initial = parseIsoDate(
    typeof props.value === 'string' ? props.value.slice(0, 10) : (props.value as null | undefined),
  );
  const key = props.dateFormatKey ?? DEFAULT_DATE_FORMAT_KEY;
  const dateFns =
    DATE_FORMAT_OPTIONS.find((o) => o.key === key)?.dateFns ??
    DATE_FORMAT_OPTIONS.find((o) => o.key === DEFAULT_DATE_FORMAT_KEY)!.dateFns;
  return (
    <div
      className="bg-popover min-w-[220px] rounded-md border p-1 shadow-md"
      onKeyDown={(e) => {
        if (e.key === 'Escape') props.stopEditing();
      }}
    >
      <DatePicker
        mode="single"
        autoFocus
        defaultOpen
        dateFormat={dateFns}
        value={initial}
        onValueChange={(date) => {
          props.onValueChange(date ? formatIsoDate(date) : null);
          props.stopEditing();
        }}
      />
    </div>
  );
}

export function MetadataDateTimeCellEditor(props: CustomCellEditorProps) {
  const ref = useFocusOnMount<HTMLInputElement>();
  // <input type="datetime-local"> wants `YYYY-MM-DDTHH:mm` (no Z, no seconds).
  // Stored ISO strings from the backend are full ISO 8601, so trim to the
  // first 16 chars for display. The server accepts any ISO date-time string
  // and normalises with `new Date(...).toISOString()` before storing.
  const initial = (() => {
    const v = props.value;
    if (typeof v !== 'string' || v === '') return '';
    return v.slice(0, 16);
  })();
  return (
    <input
      ref={ref}
      type="datetime-local"
      defaultValue={initial}
      onChange={(e) => emitOrClear(props, e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.stopEditing();
      }}
      onBlur={() => props.stopEditing()}
      className="h-full w-full border-none bg-transparent px-2 text-sm outline-none"
    />
  );
}

export function MetadataCheckBoxCellEditor(props: CustomCellEditorProps) {
  const ref = useFocusOnMount<HTMLInputElement>();
  const checked = props.value === true || props.value === 'true';
  return (
    <input
      ref={ref}
      type="checkbox"
      defaultChecked={checked}
      onChange={(e) => {
        props.onValueChange(e.target.checked ? 'true' : 'false');
        // Auto-close so a single click flips the cell value.
        props.stopEditing();
      }}
      onBlur={() => props.stopEditing()}
      className="m-2 size-4"
    />
  );
}

interface SelectOneCellEditorProps extends CustomCellEditorProps {
  options?: string[];
}

export function MetadataSelectOneCellEditor(props: SelectOneCellEditorProps) {
  const ref = useRef<HTMLSelectElement>(null);
  const options = props.options ?? [];

  useEffect(() => {
    const select = ref.current;
    if (!select) return;
    select.focus();
    try {
      select.showPicker();
    } catch {
      // Older browsers — focus is enough.
    }
  }, []);

  const rawValue = props.value as string | null | undefined;
  const value = rawValue ?? '';
  const showPlaceholder = value === '';

  return (
    <select
      ref={ref}
      value={value}
      onChange={(e) => {
        emitOrClear(props, e.target.value);
        props.stopEditing();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          props.onValueChange(null);
          props.stopEditing();
        }
      }}
      className="h-full w-full border-none bg-transparent px-2 text-sm outline-none"
      style={{ WebkitAppearance: 'none', appearance: 'none' }}
    >
      {showPlaceholder && (
        <option value="" disabled hidden>
          Select…
        </option>
      )}
      {/* Selectable path back to empty — metadata selects are never required.
          emitOrClear already maps '' → null, so onChange needs no change. */}
      <option value="">(blank)</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

// ── ENG-520: NumberWithUnitCellEditor ────────────────────────────────

/**
 * Each entry unit maps to a canonical storage unit + a multiplier. Storage
 * stays in the canonical unit (mm for metric length, inch for imperial
 * length) so cross-row comparisons line up, but the entered unit is
 * preserved alongside on the row so the planner renders the same units
 * back on read. Area / Weight have no within-system conversion — m2/sqft
 * and kg/lbs are stored as entered.
 */
const ENTRY_UNIT_TO_CANONICAL: Record<string, { canonical: string; factor: number }> = {
  mm: { canonical: 'mm', factor: 1 },
  cm: { canonical: 'mm', factor: 10 },
  m: { canonical: 'mm', factor: 1000 },
  inch: { canonical: 'inch', factor: 1 },
  ft: { canonical: 'inch', factor: 12 },
  yd: { canonical: 'inch', factor: 36 },
  m2: { canonical: 'm2', factor: 1 },
  sqft: { canonical: 'sqft', factor: 1 },
  kg: { canonical: 'kg', factor: 1 },
  lbs: { canonical: 'lbs', factor: 1 },
};

/**
 * Composite value emitted by the editor's `onValueChange`. The colDef's
 * `valueSetter` unpacks this onto two row fields atomically; the grid's
 * `onCellValueChanged` then splits it into two `handleCellEdit` calls so
 * the batch PATCH carries both fields. Null clears both fields.
 */
export interface NumberWithUnitCommit {
  /** Canonical storage value (mm/inch/m2/sqft/kg/lbs) as a string for precision. */
  value: string;
  /** Unit the user picked in the editor — preserved on the row. */
  unit: string;
}

export function normaliseEntryToCanonical(
  value: number,
  entryUnit: string,
): { value: number; unit: string } | null {
  const conv = ENTRY_UNIT_TO_CANONICAL[entryUnit];
  if (!conv) return null;
  return { value: value * conv.factor, unit: conv.canonical };
}

/**
 * Parse a pasted / programmatic scalar into a {value, unit} composite suitable
 * for the colDef's valueSetter. AG Grid hands paste payloads to valueParser as
 * raw strings; without this the number_with_unit valueSetter would reject the
 * non-composite shape and the paste would silently no-op.
 *
 * Inputs handled:
 *   - "2400 mm" / "2400mm" — explicit unit on the pasted value
 *   - "2400" — bare number → resolves the unit from the row's current unit, or
 *     the column's default unit when the row has none. Excel pastes land here.
 *   - "" / null / undefined — explicit clear (caller should null both fields)
 *
 * Returns:
 *   - { value, unit } commit on a successful parse
 *   - null on an explicit clear
 *   - undefined when the input is unparseable (caller should keep the old cell
 *     value rather than overwriting with garbage)
 *
 * Thousands separators and locale-specific decimals (comma) are intentionally
 * unsupported — ambiguous across locales.
 */
export function parseNumberWithUnitPaste(
  input: unknown,
  rowUnit: string | null,
  defaultUnit: string,
): NumberWithUnitCommit | null | undefined {
  if (input === null || input === undefined) return null;
  if (typeof input !== 'string' && typeof input !== 'number') return undefined;

  const str = String(input).trim();
  if (str === '') return null;

  const match = str.match(/^([-+]?(?:\d+(?:\.\d+)?|\.\d+))\s*([A-Za-z][A-Za-z\d]*)?$/);
  if (!match) return undefined;

  const numericPart = Number(match[1]);
  if (!Number.isFinite(numericPart)) return undefined;

  const candidateUnit = match[2] ?? rowUnit ?? defaultUnit;
  if (!candidateUnit) return undefined;

  const normalised = normaliseEntryToCanonical(numericPart, candidateUnit);
  if (!normalised) return undefined;

  return {
    value: String(normalised.value),
    unit: candidateUnit,
  };
}

/**
 * Convert a stored canonical value back to its display value using the
 * stored entry unit's factor. Stored 1000 + 'cm' → 100 (display value);
 * stored 24 + 'ft' → 2.
 */
export function canonicalToDisplayValue(
  storedValue: string | number | null,
  storedUnit: string | null,
): string {
  if (storedValue === null || storedValue === '') return '';
  if (storedUnit === null) return '';
  const factor = ENTRY_UNIT_TO_CANONICAL[storedUnit]?.factor ?? 1;
  const num = typeof storedValue === 'number' ? storedValue : Number(storedValue);
  if (!Number.isFinite(num)) return '';
  const display = num / factor;
  // Avoid trailing-zero noise: 100.0 → "100", 1.5 → "1.5". Numeric → string
  // round-trips lose JS float artefacts at common factors (10/12/36/1000).
  return String(display);
}

/**
 * Display formatter — renders `"<display value> <unit>"` from the stored
 * canonical value + entered unit. Blank when either is missing. Used by
 * the grid valueFormatter, the clipboard processor, and CSV export.
 */
export function formatNumberWithUnit(
  storedValue: string | number | null,
  storedUnit: string | null,
): string {
  if (storedValue === null || storedValue === '' || storedUnit === null) return '';
  const display = canonicalToDisplayValue(storedValue, storedUnit);
  if (display === '') return '';
  return `${display} ${storedUnit}`;
}

export interface NumberWithUnitCellEditorParams {
  /** Companion row field that stores the unit the user picked. */
  unitField: string;
  /**
   * Units the editor offers in the dropdown (the partner's enabled subset
   * of the registry's allowed_units, resolved server-side).
   */
  enabledUnits: readonly string[];
  /**
   * Pre-selected unit for empty cells. Resolved server-side from the
   * partner's saved default_unit (or the first allowed_unit when the
   * partner hasn't configured one).
   */
  defaultUnit: string;
}

type NumberWithUnitEditorProps = CustomCellEditorProps & NumberWithUnitCellEditorParams;

export function NumberWithUnitCellEditor(props: NumberWithUnitEditorProps) {
  const { unitField, enabledUnits, defaultUnit } = props;

  // Initial state pulled from the row so the editor opens showing what's
  // already stored — converted back to the user's chosen unit. An empty
  // cell pre-selects the partner's defaultUnit so the user only has to
  // type a number.
  //
  // Preserve historical units that are no longer in enabledUnits: this
  // happens when a partner narrows the unit set after data already exists.
  // The stored unit is still displayed and saved as-is unless the user
  // explicitly changes it to a now-enabled one.
  const rowData = (props.node?.data ?? {}) as Record<string, unknown>;
  const storedValueRaw = props.value;
  const storedValue =
    typeof storedValueRaw === 'string' || typeof storedValueRaw === 'number'
      ? storedValueRaw
      : null;
  const storedUnitRaw = rowData[unitField];
  const storedUnit = typeof storedUnitRaw === 'string' ? storedUnitRaw : null;
  const storedUnitOutOfSet = storedUnit !== null && !enabledUnits.includes(storedUnit);

  const initialDisplayValue =
    storedValue !== null && storedUnit !== null
      ? canonicalToDisplayValue(storedValue, storedUnit)
      : '';
  const initialUnit = storedUnit ?? defaultUnit;

  const [draftValue, setDraftValue] = useState(initialDisplayValue);
  const [draftUnit, setDraftUnit] = useState(initialUnit);
  const hasCommittedRef = useRef(false);
  const valueRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    valueRef.current?.focus();
    valueRef.current?.select();
  }, []);

  // When only one unit is enabled (and the row isn't holding a stale
  // out-of-set unit), there's nothing for the user to pick — render the
  // number input alone and let the implied unit ride along on commit.
  const singleUnitShortcut = enabledUnits.length === 1 && !storedUnitOutOfSet;

  const commit = () => {
    if (hasCommittedRef.current) return;
    hasCommittedRef.current = true;

    // Empty value → clear the cell. Pass null through onValueChange so
    // the colDef's valueSetter nulls both fields atomically.
    if (draftValue === '' || draftValue === null) {
      props.onValueChange(null);
      props.stopEditing();
      return;
    }
    const parsed = Number(draftValue);
    if (!Number.isFinite(parsed)) {
      props.stopEditing();
      return;
    }
    const normalised = normaliseEntryToCanonical(parsed, draftUnit);
    if (!normalised) {
      props.stopEditing();
      return;
    }
    // Emit a composite value. The colDef's valueSetter splits this onto
    // the two row fields, and the grid's onCellValueChanged forwards both
    // writes to the sync buffer.
    const payload: NumberWithUnitCommit = {
      value: String(normalised.value),
      unit: draftUnit,
    };
    props.onValueChange(payload);
    props.stopEditing();
  };

  return (
    <div className="flex h-full w-full items-stretch">
      <input
        ref={valueRef}
        type="number"
        step="any"
        value={draftValue}
        onChange={(e) => setDraftValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            hasCommittedRef.current = true; // prevent blur fallback
            props.stopEditing();
          }
        }}
        onBlur={(e) => {
          // Only commit if focus is leaving the editor entirely (not just
          // moving to the unit dropdown).
          if (
            e.relatedTarget &&
            (e.currentTarget.parentElement?.contains(e.relatedTarget) ?? false)
          ) {
            return;
          }
          commit();
        }}
        placeholder=""
        className={
          singleUnitShortcut
            ? 'h-full w-full border-none bg-transparent px-2 text-sm outline-none'
            : 'h-full min-w-0 flex-1 border-none bg-transparent px-2 text-sm outline-none'
        }
      />
      {!singleUnitShortcut && (
        <select
          value={draftUnit}
          onChange={(e) => setDraftUnit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              hasCommittedRef.current = true;
              props.stopEditing();
            }
          }}
          onBlur={(e) => {
            if (
              e.relatedTarget &&
              (e.currentTarget.parentElement?.contains(e.relatedTarget) ?? false)
            ) {
              return;
            }
            commit();
          }}
          className="h-full border-none bg-transparent px-1 text-xs outline-none"
          style={{ WebkitAppearance: 'none', appearance: 'none' }}
        >
          {storedUnitOutOfSet && (
            <option value={storedUnit!} disabled>
              {storedUnit} (disabled)
            </option>
          )}
          {enabledUnits.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
